import type { CSSProperties, FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { hashString } from '@/lib/format'

const PALETTE = ['#ffb89c', '#c9bbff', '#8fc9ff', '#f7c59f', '#b8e3c8']

interface GradientCoverProps {
    seed: string
    color?: string | null
    label?: string
    image?: string | null
    imageAlt?: string | null
    className?: string
    priority?: boolean
}

const point = (hash: number, shift: number, min: number, max: number): number => {
    return min + ((hash >>> shift) % (max - min))
}

/**
 * Generated cover for a journal note: soft gradient fields and a drawn route,
 * derived from the note's slug so it never changes between visits. An image
 * uploaded in the CMS replaces it.
 */
export const GradientCover: FC<GradientCoverProps> = ({
    seed,
    color,
    label,
    image,
    imageAlt,
    className,
    priority = false
}): ReactNode => {
    if (image) {
        return (
            <div className={cn('relative overflow-hidden bg-sand', className)}>
                <img
                    src={image}
                    alt={imageAlt ?? ''}
                    loading={priority ? 'eager' : 'lazy'}
                    className='absolute inset-0 h-full w-full object-cover'
                />
            </div>
        )
    }

    const hash = hashString(seed)
    const tint = color && /^#[0-9a-f]{3,8}$/i.test(color) ? color : PALETTE[hash % PALETTE.length]
    const second = PALETTE[(hash >>> 3) % PALETTE.length]

    const background: CSSProperties = {
        background: [
            `radial-gradient(circle at ${point(hash, 2, 10, 45)}% ${point(hash, 5, 15, 70)}%, ${tint} 0%, transparent 58%)`,
            `radial-gradient(circle at ${point(hash, 8, 55, 92)}% ${point(hash, 11, 30, 85)}%, ${second}b3 0%, transparent 52%)`,
            `radial-gradient(circle at ${point(hash, 14, 30, 70)}% ${point(hash, 17, 70, 100)}%, #fff4e8 0%, transparent 60%)`,
            'linear-gradient(135deg, #f6efe4 0%, #ede4d6 100%)'
        ].join(', ')
    }

    const startY = point(hash, 3, 120, 220)
    const midX = point(hash, 6, 140, 260)
    const midY = point(hash, 9, 40, 140)
    const endY = point(hash, 12, 60, 180)

    return (
        <div aria-hidden='true' className={cn('relative overflow-hidden', className)} style={background}>
            <svg viewBox='0 0 400 250' preserveAspectRatio='xMidYMid slice' className='absolute inset-0 h-full w-full' fill='none'>
                <path
                    d={`M -10 ${startY} C 80 ${startY - 60}, ${midX - 80} ${midY + 40}, ${midX} ${midY} S ${midX + 120} ${endY + 50}, 410 ${endY}`}
                    stroke='rgba(22,20,15,0.28)'
                    strokeWidth={1.2}
                    strokeLinecap='round'
                />

                <path
                    d={`M ${midX} ${midY} C ${midX + 20} ${midY + 60}, ${midX - 40} 200, ${midX - 10} 260`}
                    stroke='rgba(22,20,15,0.16)'
                    strokeWidth={1}
                    strokeDasharray='2 5'
                    strokeLinecap='round'
                />

                <circle cx={midX} cy={midY} r={8} fill='#fbf8f2' stroke='rgba(22,20,15,0.3)' />
                <circle cx={midX} cy={midY} r={3} fill='#2f37ff' />
            </svg>

            {label && (
                <span className='eyebrow absolute bottom-4 left-5 text-ink/50'>
                    {label}
                </span>
            )}
        </div>
    )
}
